import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Crown } from "@phosphor-icons/react";
import { MaskLines, Reveal, Eyebrow } from "../components/Motion";
import Testimonials from "../components/Testimonials";
import { BUSINESS, SERVICES, MANIFESTO, BIO, TEXTURES } from "../constants/site";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 hero-purple">
        <div className="absolute inset-0 radial-spot" />
        <motion.img
          style={{ y: yImg }}
          src={TEXTURES.gold}
          alt=""
          className="absolute -right-32 top-0 h-full w-[45%] object-cover opacity-15 mix-blend-screen pointer-events-none"
        />

        <div className="relative z-10 max-w-[1440px] mx-auto w-full px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
          {/* LEFT: copy */}
          <motion.div style={{ y: yText, opacity }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mb-8"
            >
              <img src={BUSINESS.logo} alt="Creative Designs Services" className="h-32 sm:h-40 w-auto drop-shadow-[0_20px_50px_rgba(212,175,55,0.25)]" />
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="flex items-center gap-3">
              <Crown size={18} weight="fill" className="text-[color:var(--gold)]" />
              <Eyebrow>{BUSINESS.subtitle}</Eyebrow>
            </motion.div>

            <h1 className="font-display font-light leading-[0.9] text-[color:var(--text)] mt-5 text-[clamp(2.8rem,7vw,6.5rem)]">
              <MaskLines lines={["Creative Designs"]} delay={0.45} />
              <span className="line-mask">
                <motion.span
                  className="block italic text-gradient-gold"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  Services
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9 }}
              className="max-w-lg text-[color:var(--text-2)] text-base sm:text-lg leading-relaxed mt-8"
            >
              A boutique Bahamian studio for administrative & creative solutions — design, writing,
              academic support and applications, all delivered with quiet luxury and care.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.9 }}
              className="flex flex-wrap items-center gap-5 mt-10"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--gold)] px-8 py-4 text-[#0b0710] font-medium tracking-wide transition-all duration-500 hover:bg-[color:var(--gold-light)]"
              >
                Request a Quote
                <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />
              </Link>
              <Link
                to="/portfolio"
                data-testid="hero-cta-work"
                className="link-underline text-[color:var(--text-2)] hover:text-[color:var(--text)] transition-colors"
              >
                View our work
              </Link>
            </motion.div>
          </motion.div>

          {/* RIGHT: founder portrait */}
          <motion.div style={{ y: yImg }} className="relative mt-10 lg:mt-0 max-w-sm mx-auto lg:max-w-none w-full">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)", opacity: 0.4 }}
              animate={{ clipPath: "inset(0% 0 0 0)", opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative arch overflow-hidden border border-[color:var(--gold)]/30 gold-glow"
            >
              <img src={BUSINESS.founderPhoto} alt={BUSINESS.founder} data-testid="founder-image-hero" className="w-full h-[440px] sm:h-[560px] object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg)]/70 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap glass-nav rounded-full px-7 py-3 text-center"
            >
              <span className="font-display text-lg text-[color:var(--text)]">{BUSINESS.founder}</span>
              <span className="block font-mono-x text-[9px] tracking-[0.3em] uppercase text-[color:var(--gold)]">Founder & Creative Professional</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE RIBBON */}
      <section className="relative border-y hairline bg-[color:var(--purple-deep)] py-6">
        <Marquee speed={40} gradient={false} autoFill>
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="font-display italic text-3xl sm:text-4xl text-[color:var(--gold-light)] mx-8 flex items-center gap-8">
              {BUSINESS.tagline}
              <span className="text-[color:var(--gold)] not-italic text-xl">✦</span>
              {BUSINESS.subtitle}
              <span className="text-[color:var(--gold)] not-italic text-xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 sm:py-36">
        <div className="grid lg:grid-cols-[0.9fr_1.4fr] gap-12 lg:gap-20">
          <Reveal>
            <Eyebrow>Our Approach</Eyebrow>
            <h2 className="font-display font-light text-5xl sm:text-6xl mt-5 leading-[1] text-[color:var(--text)]">
              From stress<br />to <span className="italic text-gradient-gold">success</span>
            </h2>
            <p className="text-[color:var(--text-2)] mt-6 max-w-sm leading-relaxed">
              Three principles guide everything we make — a calm, deliberate process built entirely around you.
            </p>
          </Reveal>

          <div className="divide-y hairline border-t hairline">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1}>
                <div className="group grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 py-10 transition-colors duration-500">
                  <span className="font-mono-x text-sm text-[color:var(--gold)] pt-2">{m.num}</span>
                  <div>
                    <h3 className="font-display text-3xl sm:text-4xl text-[color:var(--text)] transition-transform duration-500 group-hover:translate-x-2">
                      {m.title}
                    </h3>
                    <p className="text-[color:var(--text-2)] mt-3 max-w-xl leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE FOUNDER */}
      <section className="relative overflow-hidden border-t hairline">
        <div className="absolute inset-0 radial-spot opacity-70" />
        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative max-w-sm mx-auto lg:mx-0">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className="relative arch overflow-hidden border border-[color:var(--gold)]/30 gold-glow"
              >
                <img src={BUSINESS.founderPhoto} alt={BUSINESS.founder} data-testid="founder-image-home" className="w-full h-[420px] sm:h-[500px] object-cover object-top" />
              </motion.div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <Eyebrow>Meet the Founder</Eyebrow>
              <h2 className="font-display font-light text-5xl sm:text-6xl mt-5 leading-[1] text-[color:var(--text)]">
                {BUSINESS.founder.replace("Mrs. ", "")}
              </h2>
              <p className="font-mono-x text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mt-3">
                Founder & Creative Professional · {BUSINESS.location}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[color:var(--text-2)] text-base sm:text-lg leading-relaxed mt-7 max-w-xl">
                {BIO.paragraphs[0]}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                data-testid="home-meet-founder-cta"
                className="group inline-flex items-center gap-3 mt-9 rounded-full border border-[color:var(--gold)] px-8 py-3.5 text-[color:var(--gold)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0b0710]"
              >
                Read her story
                <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="relative bg-[color:var(--surface)] border-y hairline">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <Reveal>
              <Eyebrow>What we do</Eyebrow>
              <h2 className="font-display font-light text-5xl sm:text-6xl mt-4 text-[color:var(--text)]">Services</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services" data-testid="services-teaser-all" className="link-underline text-[color:var(--gold)] flex items-center gap-2">
                All services <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-[color:var(--border)]/40">
            {SERVICES.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.key}`}
                  className="group block h-full bg-[color:var(--surface)] hover:bg-[color:var(--surface-2)] p-10 sm:p-12 transition-colors duration-500"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono-x text-sm text-[color:var(--gold)]">{s.num}</span>
                    <ArrowUpRight size={22} className="text-[color:var(--muted)] group-hover:text-[color:var(--gold)] transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </div>
                  <h3 className="font-display text-4xl mt-8 text-[color:var(--text)]">{s.title}</h3>
                  <p className="text-[color:var(--text-2)] mt-4 leading-relaxed max-w-md">{s.blurb}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.items.slice(0, 4).map((it) => (
                      <span key={it} className="text-xs text-[color:var(--muted)] border hairline rounded-full px-3 py-1">{it}</span>
                    ))}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-28 sm:py-40 text-center">
        <Reveal>
          <Eyebrow>Let's begin</Eyebrow>
          <h2 className="font-display font-light text-[clamp(2.5rem,7vw,6rem)] leading-[1] mt-6 text-[color:var(--text)]">
            Ready to trade stress<br />for <span className="italic text-gradient-gold">success?</span>
          </h2>
          <Link
            to="/contact"
            data-testid="home-cta-bottom"
            className="group inline-flex items-center gap-3 mt-12 rounded-full border border-[color:var(--gold)] px-10 py-4 text-[color:var(--gold)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0b0710]"
          >
            Start your project
            <ArrowRight className="transition-transform duration-500 group-hover:translate-x-1" size={18} weight="bold" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
